import React, { FC } from 'react';
import { ApolloClient, InMemoryCache, ApolloProvider, ApolloLink, HttpLink } from '@apollo/client';
import fetch from 'isomorphic-unfetch';
import { v4 as uuidv4 } from 'uuid';
import { isLoggedIn, getSiteId, getAccessToken, getGuid, getAkamaiOriginSessionCookie } from 'Utils/auth';
import { useDetermineLocale } from './hooks';
import { environment } from '@/data/constants';

const enableApolloDevTools = process.env.NODE_ENV != environment.PRODUCTION;

interface IApolloConfig {
  children: React.ReactNode;
}

const ApolloConfig: FC<IApolloConfig> = ({ children }) => {
  const language = useDetermineLocale();
  const addOperationNameToHeaders = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers }: { headers: Record<string, string> }) => ({
      headers: {
        operationName: operation.operationName,
        ...headers,
        language,
      },
    }));
    return forward(operation);
  });

  const getValidHeaders = () => {
    const requiredHeaders: any = {
      Authorization: isLoggedIn(),
      language,
      'x-lcl-apikey': process.env.NEXT_PUBLIC_API_KEY,
      requestid: `FE-${uuidv4()}`,
      site: getSiteId(),
      cartId: getGuid(),
      accessToken: getAccessToken(),
    };
    const Origin_Session_Header = getAkamaiOriginSessionCookie();
    if (Origin_Session_Header) {
      return {
        ...requiredHeaders,
        Origin_Session_Header,
      };
    } else {
      return requiredHeaders;
    }
  };

  const httpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URL,
    headers: getValidHeaders(),
    fetch,
  });
  const client = new ApolloClient({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URL,
    cache: new InMemoryCache(),
    link: ApolloLink.from([addOperationNameToHeaders, httpLink]),
    connectToDevTools: enableApolloDevTools,
  });

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default ApolloConfig;
